import { useState, useCallback, useMemo } from 'react';
import { Button, toast } from '@leadsnavi/ui-kit';
import { useMemoizedFn } from 'ahooks';
import mixpanel from 'mixpanel-browser';
import { SearchCardV2SubMsg } from '@/features/chat-messages/types';
import {
  transSubMsgV2ToSupplier,
  transSubMsgV2ToAgentStartReq,
  transAgentStartReqToModifySearchCardReq,
} from '@/features/chat-messages/types';
import { agentStartPost, agentReplacePost, agentSavePost } from '@/services/swagger/easourcing/AgentInquiryService';
import { IApiAgentStartReq, IApiInquiryDto, IApiModifySearchCardReq } from '@/services/swagger/easourcing/models';
import { BuyerProfile } from '@/components/send-inquiry-dialog/types';
import ActiveTaskLimitModal from '@/components/active-task-limit-modal';
import { SendInquiryDialogPortal } from '@/components/send-inquiry-dialog';
import { ensureLogin } from '@/utils/auth';
import { chatNotification } from '@/features/chat-messages/store/chat-store';

type SupplierCardActionsProps = {
  /** 搜索卡片消息内容 */
  subMsg: SearchCardV2SubMsg;
  /** 当前会话 ID */
  sessionId?: string;
  /** 外层消息 ID，用于构造 modifySearchCard 请求 */
  messageId?: string | number;
  /** 是否正在执行修改轮询（外部控制），true 时两个按钮均置灰 */
  isModifying?: boolean;
  /** 用户提交修改表单后的回调，参数为已构建的修改请求体 */
  onModifySubmit?: (req: IApiModifySearchCardReq) => void;
};

/** 推荐供应商卡片底部操作按钮组 */
export function SupplierCardActions({
  subMsg,
  sessionId,
  messageId,
  isModifying,
  onModifySubmit,
}: SupplierCardActionsProps) {
  const [agentLoading, setAgentLoading] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [runningInquiries, setRunningInquiries] = useState<IApiInquiryDto[]>([]);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  /** 暂存超限时的请求数据，供「Replace / Save」时使用 */
  const [pendingInquiryData, setPendingInquiryData] = useState<IApiAgentStartReq | null>(null);
  const [limitLoading, setLimitLoading] = useState(false);

  /**
   * 为 SendInquiryDialogPortal 准备 suppliers 数据
   * 修正 messageId，使 Dialog 内部 buildInquiryRequest 生成正确的 sessionId
   */
  const suppliersForDialog = useMemo(() => {
    const supplier = transSubMsgV2ToSupplier(subMsg);
    // transSubMsgToSupplier 在无已选供应商时会将 messageId 置空，
    // 这会导致 Dialog 内部 buildInquiryRequest 中 sessionId 被覆盖为空字符串。
    // 这里用外部传入的 sessionId 进行修正。
    return [{ ...supplier, messageId: sessionId || supplier.messageId }];
  }, [subMsg, sessionId]);

  /**
   * 从 subMsg.contentValue.buyerProfile 提取买家信息，作为 Dialog 的初始反显数据
   */
  const initialBuyerProfile = useMemo<Partial<BuyerProfile> | undefined>(() => {
    const bp = subMsg.contentValue.buyerProfile;
    if (!bp) return undefined;
    return {
      name: bp.name || '',
      location: bp.location || '',
      companyName: bp.companyName || '',
      industry: bp.industry || '',
      companyIntroduction: bp.companyIntroduction || '',
    };
  }, [subMsg]);

  /**
   * 为 SendInquiryDialogPortal 准备 sendInquiryParams
   * 补齐 handleSendInquiryByAgent（transSubMsgToAgentStartReq）中包含、
   * 但 Dialog 内部 buildInquiryRequest 不会构建的字段：targetCountry、otherLanguages
   */
  const sendInquiryParams = useMemo<Partial<IApiAgentStartReq>>(() => {
    const cv = subMsg.contentValue;
    return {
      sessionId,
      targetCountry: cv.condition.country,
    };
  }, [sessionId, subMsg]);

  /** 统一的 agentStart 调用 + 超限处理逻辑 */
  const doAgentStart = useCallback(async (req: IApiAgentStartReq) => {
    const res = await agentStartPost(req, { skipGlobalErrorHandling: true });
    const runningList = (res?.data?.runningInquiries || []) as IApiInquiryDto[];

    if (runningList.length >= 3) {
      setPendingInquiryData(req);
      setRunningInquiries(runningList);
      setShowLimitModal(true);
      return;
    }
    chatNotification.emit('sendInquirySuccess', {
      inquiryId: res.data.inquiryId,
      supplierId: '',
      type: 'save',
    });
    toast.success('Inquiry started successfully');
  }, []);

  /**
   * 点击「Send inquiry by Agent」——直接发送（不经过弹窗编辑）。
   * subMsg 是唯一数据来源，始终从最新的 subMsg 构建请求参数。
   * （表单修改后会触发 modifySearchCard → 轮询 → updateSearchCardSubMsg → 组件重挂载，
   *   重挂载后 subMsg 已是最新数据，无需额外合并弹窗表单数据。）
   */
  const handleSendInquiryByAgent = useMemoizedFn(async () => {
    mixpanel.track('searchcard_sendbyagent_click');
    try {
      await ensureLogin();
    } catch {
      return;
    }

    setAgentLoading(true);
    try {
      const req = transSubMsgV2ToAgentStartReq(subMsg, sessionId);
      await doAgentStart(req);
    } catch (error: unknown) {
      const apiError = error as { message?: string };
      toast.error(apiError?.message || 'Failed to start inquiry');
    } finally {
      setAgentLoading(false);
    }
  });

  /**
   * SendInquiryDialog 的 onSubmit 回调：参数已由 Dialog 校验合法。
   * - 关闭弹窗
   * - 若外部传入了 onModifySubmit，则构建 IApiModifySearchCardReq 并调用，触发 modify-search-card 流程
   *   （modifySearchCard 完成后后端会返回新 subMsg，组件重挂载后数据自动更新）
   */
  const handleInquiryDialogSubmit = useMemoizedFn((req: IApiAgentStartReq, buyerProfile: BuyerProfile) => {
    setShowInquiryDialog(false);

    if (onModifySubmit) {
      const modifyReq = transAgentStartReqToModifySearchCardReq(
        {
          ...transSubMsgV2ToAgentStartReq(subMsg, sessionId),
          ...req,
        },
        messageId ?? '',
        sessionId ?? '',
        subMsg.subMessageId ?? ''
      );
      onModifySubmit({
        ...modifyReq,
        buyerProfile: {
          ...subMsg.contentValue.buyerProfile,
          name: buyerProfile.name,
          location: buyerProfile.location,
          companyName: buyerProfile.companyName,
          industry: buyerProfile.industry,
          companyIntroduction: buyerProfile.companyIntroduction,
        },
      });
    }
  });

  /** 超限弹窗 —— 替换某个正在运行的任务 */
  const handleReplaceTask = useMemoizedFn(async (inquiryId: string) => {
    if (!pendingInquiryData) return;
    setLimitLoading(true);
    try {
      const res = await agentReplacePost({ ...pendingInquiryData, stopInquiryId: inquiryId });
      toast.success('Sending Started!');
      setShowLimitModal(false);
      setPendingInquiryData(null);
      setShowInquiryDialog(false);
      chatNotification.emit('sendInquirySuccess', {
        inquiryId: res.data.inquiryId,
        supplierId: '',
        type: 'save',
      });
    } catch (e) {
      toast.error((e as Error)?.message || 'Failed to replace inquiry');
    } finally {
      setLimitLoading(false);
    }
  });

  /** 超限弹窗 —— 保存到队列 */
  const handleSaveToQueue = useMemoizedFn(async () => {
    if (!pendingInquiryData) return;
    setLimitLoading(true);
    try {
      await agentSavePost(pendingInquiryData);
      toast.success('Task Saved.');
      setShowLimitModal(false);
      setPendingInquiryData(null);
      setShowInquiryDialog(false);
    } catch (e) {
      toast.error((e as Error)?.message || 'Failed to save inquiry');
    } finally {
      setLimitLoading(false);
    }
  });

  /** 点击「Modify Requires」 */
  const handleModifyRequires = useMemoizedFn(async () => {
    mixpanel.track('searchcard_modifyinquiry_click');
    try {
      await ensureLogin();
    } catch {
      return;
    }
    setShowInquiryDialog(true);
  });

  return (
    <>
      <div className="mt-2 flex gap-3 justify-end">
        {/* 修改需求：轮询中置灰并附加 loading 态 */}
        <Button
          variant="outline"
          className="h-9"
          disabled={!!isModifying}
          loading={!!isModifying}
          onClick={handleModifyRequires}
        >
          Modify Requests
        </Button>
        {/* 通过 Agent 发送询盘：轮询中或 agent 执行中均置灰 */}
        <Button
          className="h-9"
          loading={agentLoading}
          disabled={agentLoading || !!isModifying}
          onClick={handleSendInquiryByAgent}
        >
          Send inquiry by Agent
        </Button>
      </div>

      {/* 超限弹窗 —— 统一管理超限逻辑 */}
      <ActiveTaskLimitModal
        open={showLimitModal}
        runningInquiries={runningInquiries}
        onReplace={handleReplaceTask}
        onSave={handleSaveToQueue}
        loading={limitLoading}
        onCancel={() => {
          setShowLimitModal(false);
          setPendingInquiryData(null);
        }}
        onOpenChange={o => {
          setShowLimitModal(o);
          if (!o) setPendingInquiryData(null);
        }}
      />

      {/* 修改需求 / 发送询盘弹窗 */}
      <SendInquiryDialogPortal
        open={showInquiryDialog}
        suppliers={suppliersForDialog}
        sendInquiryParams={sendInquiryParams}
        initialBuyerProfile={initialBuyerProfile}
        onSubmit={handleInquiryDialogSubmit}
        onOpenChange={setShowInquiryDialog}
        onCancel={() => setShowInquiryDialog(false)}
        onSuccess={inquiry => {
          chatNotification.emit('sendInquirySuccess', inquiry);
        }}
      />
    </>
  );
}
